import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Table, notification, Skeleton } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import SidebarPage from '../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../layout/PageTitle/PageTitle';
import { useGet, usePost } from '../../../hooks/useApi';
import {
  ReportAccount,
  CreditAccountsResponse,
  ExportResponse,
} from '../../../models/admin/reports.model';

const floatToDollar = (value: number): string => {
  return value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const CreditAccounts: FC = () => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Fetch credit accounts
  const { data, isLoading, error } = useGet<CreditAccountsResponse>(
    '/v1/auth/admin/reports/credit'
  );

  // Export mutation
  const exportMutation = usePost<ExportResponse, void>(
    '/v1/auth/admin/reports/credit/export'
  );

  const handleExport = () => {
    setIsExporting(true);
    exportMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.downloadUrl) {
          window.open(response.downloadUrl, '_blank');
          notification.success({
            message: 'Export Started',
            description: 'Your CSV file is ready for download.',
          });
        }
        setIsExporting(false);
      },
      onError: (error: any) => {
        notification.error({
          message: 'Export Failed',
          description:
            error.response?.data?.message ||
            'Failed to export credit accounts.',
        });
        setIsExporting(false);
      },
    });
  };

  const handleRowClick = (record: ReportAccount) => {
    navigate(`/admin/ledger/${record.primaryId}`);
  };

  const columns: ColumnsType<ReportAccount> = [
    {
      title: 'Primary Name',
      dataIndex: 'primaryName',
      key: 'primaryName',
      sorter: (a, b) => a.primaryName.localeCompare(b.primaryName),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => {
        const isNegative = balance < 0;
        const absValue = Math.abs(balance);
        const formatted = floatToDollar(absValue);
        return (
          <span style={{ color: isNegative ? 'green' : 'inherit' }}>
            {isNegative ? '-' : ''}${formatted}
          </span>
        );
      },
      sorter: (a, b) => a.balance - b.balance,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'School',
      dataIndex: 'schoolName',
      key: 'schoolName',
      sorter: (a, b) => a.schoolName.localeCompare(b.schoolName),
    },
  ];

  if (error) {
    return (
      <SidebarPage>
        <PageTitle>Credit Accounts</PageTitle>
        <Card>
          <p>Error loading credit accounts. Please try again.</p>
        </Card>
      </SidebarPage>
    );
  }

  return (
    <SidebarPage>
      <PageTitle>Credit Accounts</PageTitle>

      <Card>
        {isLoading ? (
          <Skeleton active />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <strong>Total Credit Accounts: {data?.totalCount || 0}</strong>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={isExporting}
                disabled={isLoading || !data || data.accounts.length === 0}
              >
                Download CSV
              </Button>
            </div>
            <Table
              columns={columns}
              dataSource={data?.accounts || []}
              rowKey="primaryId"
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} accounts`,
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' },
              })}
            />
          </>
        )}
      </Card>
    </SidebarPage>
  );
};

export default CreditAccounts;
